// Mock Farm and Station Data for Map View
// Uses data from projectsData.js and mockStationsData.js

import { PROJECTS_DATA } from './projectsData';
import { MOCK_STATIONS } from './mockStationsData';

// Calculate bounds from polygon or stations, with optional center fallback
const calculateBounds = (polygon, stations, center) => {
  // Use polygon if available
  if (polygon && polygon.length > 0) {
    const lats = polygon.map(p => p[0]);
    const lons = polygon.map(p => p[1]);
    
    return {
      north: Math.max(...lats) + 0.001,
      south: Math.min(...lats) - 0.001,
      east: Math.max(...lons) + 0.001,
      west: Math.min(...lons) - 0.001
    };
  }
  
  // Fallback to stations
  if (stations && stations.length > 0) {
    const lats = stations.map(s => s.locationLat);
    const lons = stations.map(s => s.locationLon);
    
    return {
      north: Math.max(...lats) + 0.005,
      south: Math.min(...lats) - 0.005,
      east: Math.max(...lons) + 0.005,
      west: Math.min(...lons) - 0.005
    };
  }
  
  // If we have a center coordinate but no polygon/stations, build a tiny box
  // Consumers will still clamp zoom via fitBounds options.
  if (center && center.length === 2) {
    const [lat, lon] = center;
    const delta = 0.002;
    return {
      north: lat + delta,
      south: lat - delta,
      east: lon + delta,
      west: lon - delta
    };
  }
  
  return null;
};

// Group stations by project and create farm/project objects
const createProjectsFromStations = () => {
  // Group stations by projectId
  const stationsByProject = MOCK_STATIONS.reduce((acc, station) => {
    const projectId = station.projectId || station.farmId;
    if (!acc[projectId]) {
      acc[projectId] = [];
    }

    // Ensure station has sensorType field (alias for stationType)
    const enrichedStation = {
      ...station,
      sensorType: station.sensorType || station.stationType
    };

    acc[projectId].push(enrichedStation);
    return acc;
  }, {});

  // Create project/farm objects with full metadata from PROJECTS_DATA
  return PROJECTS_DATA.map(project => {
    const stations = stationsByProject[project.id] || [];
    const activeStations = stations.filter(s => s.status === 'active').length;
    const bounds = calculateBounds(project.polygon, stations, project.center);

    return {
      // Core identification (keep 'id' for backwards compatibility)
      id: project.id,
      name: project.title,
      title: project.title,
      location: project.location,
      
      // Project metadata
      status: project.status,
      power: project.power,
      area: project.area,
      crop: project.crop,
      technology: project.technology,
      description: project.description,
      excludedStationNumbers: project.excludedStationNumbers,
      
      // Map configuration
      centerCoordinates: {
        lat: project.center[0],
        lon: project.center[1]
      },
      center: project.center,
      zoom: project.zoom,
      bounds,
      
      // Polygon boundary
      polygon: project.polygon,
      sections: project.sections,
      
      // Station positions
      masterStation: project.masterStation,
      slaveStations: project.slaveStations,
      
      // Station data
      totalStations: stations.length,
      activeStations,
      stationsCount: project.stationsCount,
      stations
    };
  });
};

export const mapFarms = createProjectsFromStations();

// Alias for new naming convention
export const mapProjects = mapFarms;

// Export helper to get station counts by status
export const getStationCounts = (stations) => {
  const campoAperto = stations.filter(s => s.sensorType === 'campo_aperto');
  const retroModulo = stations.filter(s => s.sensorType === 'retro_modulo');
  const masterStations = stations.filter(s => s.isMaster);
  const slaveStations = stations.filter(s => !s.isMaster);
  
  return {
    total: stations.length,
    active: stations.filter(s => s.status === 'active').length,
    warning: stations.filter(s => s.status === 'warning').length,
    inactive: stations.filter(s => s.status === 'inactive').length,
    master: masterStations.length,
    slave: slaveStations.length,
    campoAperto: {
      total: campoAperto.length,
      active: campoAperto.filter(s => s.status === 'active').length,
      warning: campoAperto.filter(s => s.status === 'warning').length,
      inactive: campoAperto.filter(s => s.status === 'inactive').length
    },
    retroModulo: {
      total: retroModulo.length,
      active: retroModulo.filter(s => s.status === 'active').length,
      warning: retroModulo.filter(s => s.status === 'warning').length,
      inactive: retroModulo.filter(s => s.status === 'inactive').length
    }
  };
};

// Export helper to filter stations
export const filterStations = (stations, filters) => {
  let filtered = [...stations];

  // Filter by status
  if (filters.status && filters.status.length > 0) {
    filtered = filtered.filter(s => filters.status.includes(s.status));
  }

  // Filter by type
  if (filters.stationType && filters.stationType !== 'all') {
    if (filters.stationType === 'master') {
      filtered = filtered.filter(s => s.isMaster);
    } else if (filters.stationType === 'slave') {
      filtered = filtered.filter(s => !s.isMaster);
    } else {
      filtered = filtered.filter(s => s.sensorType === filters.stationType);
    }
  }

  // Filter by search query
  if (filters.searchQuery) {
    const query = filters.searchQuery.toLowerCase();
    filtered = filtered.filter(s =>
      s.stationCode.toLowerCase().includes(query) ||
      s.locationDescription.toLowerCase().includes(query) ||
      (s.projectTitle && s.projectTitle.toLowerCase().includes(query))
    );
  }

  return filtered;
};

// Get project by ID
export const getProjectById = (id) => mapFarms.find(p => p.id === id);

// Get all projects
export const getAllProjects = () => mapFarms;
